import React from 'react';
import {
  Buildings,
  MapPin,
  Calendar,
  Users,
  PawPrint,
  Wine,
  Sparkle,
  CheckCircle,
  Star,
} from '@phosphor-icons/react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';

const facts = [
  { icon: Buildings, label: 'Industry', value: 'Pet Services' },
  { icon: Users, label: 'Team size', value: '11–50 employees' },
  { icon: MapPin, label: 'Headquarters', value: 'Bangalore, Karnataka' },
  { icon: Calendar, label: 'Founded', value: '2015' },
];

const ventures = [
  {
    icon: PawPrint,
    title: 'PetCart Nest',
    year: 'Launched 2018',
    body:
      "India's first pet resort — boarding, swimming, hydrotherapy and socialization, set in a green eco-system.",
  },
  {
    icon: Wine,
    title: 'Wag & Wine',
    year: 'Launched 2023',
    body:
      "India's first wine cafe for pet parents and pet lovers — a space built for people and their pets to unwind together.",
  },
  {
    icon: Sparkle,
    title: "What's next",
    year: 'On the roadmap',
    body:
      'Expanding into pet essentials, wellness and logistics — building the full lifecycle, one category at a time.',
  },
];

const stats = [
  { value: '14,000+', label: 'Pet days hosted' },
  { value: '1,200+', label: 'Swim sessions delivered' },
  { value: '75+', label: 'Pet birthdays celebrated' },
  { value: '4.7', label: '1,100+ Google reviews', google: true },
];

const specialties = [
  'First pet resort in India',
  'Pet resort in a green eco-system',
  'First exclusive birthday corner for pets',
  'First wine cafe for pet parents and pet lovers',
];

const About = () => (
  <div className="min-h-screen bg-white overflow-x-hidden">
    <Header />

    {/* Hero */}
    <section className="relative h-[480px] min-h-[420px] overflow-hidden bg-neutral-950 pt-14 sm:pt-16 lg:h-[72vh] lg:min-h-[560px]">
      <img
        src="https://images.unsplash.com/photo-1450778869180-41d0601e046e?auto=format&fit=crop&w=2000&q=80"
        alt="PetCart — caring for pets across their lifecycle"
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/30 to-black/75" />
      <div className="absolute inset-0 z-10 mx-auto flex max-w-4xl flex-col items-center justify-center px-5 pt-[140px] text-center text-white sm:pt-[150px] lg:pt-[160px]">
        <div className="landing-hero-eyebrow">About PetCart</div>
        <h1 className="landing-hero-title mt-1">A lifestyle brand for the lifecycle of pets</h1>
        <p className="landing-hero-lede mt-4">
          From resorts to everyday care, we're building the categories pet parents in India have been waiting for.
        </p>
      </div>
    </section>

    {/* Intro */}
    <section className="bg-white pt-14 pb-4 sm:pt-20">
      <div className="mx-auto max-w-3xl px-4 text-center">
        <p className="landing-intro-lede">
          PetCart is a lifestyle brand focused on comprehensive pet care across every stage of a pet's life.
          We design spaces, services and experiences that pets enjoy and parents trust — from a few hours of
          play to long stays, special days, and everything in between.
        </p>
      </div>
    </section>

    {/* Company facts */}
    <section className="bg-white pt-10 pb-16 sm:pt-12 sm:pb-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
            Company
          </span>
          <h2 className="landing-section-title">The basics</h2>
          <p className="landing-section-lede mx-auto">
            Blends Pet Solutions Private Ltd — privately held, headquartered in Bangalore.
          </p>
        </div>

        <div className="landing-bento-surface grid grid-cols-1 overflow-hidden rounded-2xl border border-neutral-200 bg-white sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((tile, idx) => {
            const Icon = tile.icon;
            return (
              <div
                key={tile.label}
                className={[
                  'p-5 sm:p-6',
                  idx > 0 ? 'sm:border-l sm:border-neutral-200' : '',
                ].join(' ')}
              >
                <Icon size={22} weight="duotone" className="mb-3 text-primary" />
                <p className="landing-card-label">{tile.label}</p>
                <p className="landing-card-meta mt-1">{tile.value}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Our story / ventures */}
    <section className="bg-neutral-100 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
            Our story
          </span>
          <h2 className="landing-section-title">Built category by category</h2>
          <p className="landing-section-lede mx-auto">
            We don't add services — we open categories. Each one designed to fit a real gap in how pets live in India today.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ventures.map((v) => {
            const Icon = v.icon;
            return (
              <div
                key={v.title}
                className="rounded-2xl border border-neutral-200 bg-white p-6"
              >
                <Icon size={26} weight="duotone" className="mb-4 text-primary" />
                <p className="landing-card-label">{v.title}</p>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-primary">
                  {v.year}
                </p>
                <p className="landing-card-meta mt-3">{v.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Stats */}
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
            Impact
          </span>
          <h2 className="landing-section-title">Our journey in numbers</h2>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((s) =>
            s.google ? (
              <div
                key={s.label}
                className="flex items-center gap-4 rounded-2xl border border-neutral-200 bg-white p-6 text-left"
              >
                <svg viewBox="0 0 48 48" className="h-10 w-10 shrink-0" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-semibold text-neutral-900 sm:text-3xl">{s.value}</p>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          weight="fill"
                          className={i < 4 ? 'text-yellow-400' : 'text-yellow-400/60'}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="landing-card-meta mt-1">{s.label}</p>
                </div>
              </div>
            ) : (
              <div
                key={s.label}
                className="rounded-2xl border border-neutral-200 bg-white p-6 text-center"
              >
                <p className="text-3xl font-semibold text-neutral-900 sm:text-4xl">{s.value}</p>
                <p className="landing-card-meta mt-2">{s.label}</p>
              </div>
            )
          )}
        </div>
      </div>
    </section>

    {/* Specialties */}
    <section className="bg-neutral-100 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="landing-section-header">
          <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
            Specialties
          </span>
          <h2 className="landing-section-title">A few firsts we're proud of</h2>
        </div>

        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {specialties.map((item) => (
            <li
              key={item}
              className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white p-5 text-sm font-medium text-neutral-700"
            >
              <CheckCircle size={20} weight="fill" className="mt-0.5 flex-none text-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    <Footer />
  </div>
);

export default About;
